import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import Landing from "@/pages/Landing";
import { captureFirstTouch } from "@/lib/attribution";
import { SEARCH_MODE } from "@/lib/searchConfig";

// Lazy-loaded so the search bundle never lands in the homepage's initial JS
const SearchPage = lazy(() => import("@/pages/Search"));
import About from "@/pages/About";
import MarkShaikh from "@/pages/MarkShaikh";
import Admin from "@/pages/Admin";
import CreditGuide from "@/pages/CreditGuide";
import Privacy from "@/pages/Privacy";
import Complaints from "@/pages/Complaints";
import SuburbPage from "@/pages/SuburbPage";
import FinanceCategoryPage from "@/pages/FinanceCategoryPage";
import Insights from "@/pages/Insights";
import InsightPost from "@/pages/InsightPost";
import NotFound from "@/pages/NotFound";
import Analytics from "@/components/Analytics";
import ChatAssistant from "@/components/ChatAssistant";
import AppFrame from "@/components/AppFrame";
import ErrorBoundary from "@/components/ErrorBoundary";

// Hard-redirect /signatures (no filename) → the static install hub.
// React Router intercepts directory URLs in production, so we bounce them
// to the actual hosted index.html via a full-page navigation.
function SignaturesRedirect({ to }) {
  useEffect(() => { window.location.replace(to); }, [to]);
  return null;
}

function App() {
  useEffect(() => { captureFirstTouch(); }, []);
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <div className="App">
          <Analytics />
          <BrowserRouter>
            <AppFrame>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/about" element={<About />} />
                <Route path="/about/mark-shaikh" element={<MarkShaikh />} />
                {SEARCH_MODE && (
                  <Route path="/search" element={<Suspense fallback={<div className="min-h-screen bg-[#050505]" />}><SearchPage /></Suspense>} />
                )}
                <Route path="/admin" element={<Admin />} />
                <Route path="/credit-guide" element={<CreditGuide />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/complaints" element={<Complaints />} />
                <Route path="/finance/:slug" element={<FinanceCategoryPage />} />
                <Route path="/locations/:slug" element={<SuburbPage />} />
                <Route path="/finance-broker/:slug" element={<SuburbPage />} />
                <Route path="/insights" element={<Insights />} />
                <Route path="/insights/:slug" element={<InsightPost />} />
                <Route path="/signatures" element={<SignaturesRedirect to="/signatures/index.html" />} />
                <Route path="/signatures/" element={<SignaturesRedirect to="/signatures/index.html" />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </AppFrame>
          </BrowserRouter>
          <ChatAssistant />
          <Toaster
            theme="dark"
            position="top-center"
            toastOptions={{
              style: {
                background: "#0a0a0a",
                border: "1px solid #27272a",
                color: "#fff",
              },
            }}
          />
        </div>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
